import React, { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';

import useCart from 'hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useCurrentDispensary } from 'src/dispensary/hooks/use-current-dispensary';
import { MENU_TYPE_MED, MENU_TYPE_REC } from 'shared/constants';
import { isMedOnly } from 'shared/core/helpers/dispensaries';

export type CatalogSurface = 'board' | 'cms' | 'consumer';
export type CatalogMenuType = string;

export type CatalogRequestContextValue = {
  menuType: CatalogMenuType;
  orderType: string;
  ready: boolean;
  surface: CatalogSurface;
};

const CatalogRequestContext = createContext<CatalogRequestContextValue>({
  menuType: MENU_TYPE_REC,
  orderType: '',
  ready: true,
  surface: 'consumer',
});

export function CatalogRequestProvider({
  children,
  value,
}: PropsWithChildren<{ value: CatalogRequestContextValue }>): JSX.Element {
  return <CatalogRequestContext.Provider value={value}>{children}</CatalogRequestContext.Provider>;
}

export function useCatalogRequestContext(): CatalogRequestContextValue {
  return useContext(CatalogRequestContext);
}

export function ConsumerCatalogRequestProvider({ children }: PropsWithChildren): JSX.Element {
  const cart = useCart();
  const value = useObserver<CatalogRequestContextValue>(() => ({
    menuType: cart.menuType,
    orderType: cart.simplifiedOrderType ?? '',
    ready: true,
    surface: 'consumer',
  }));

  return <CatalogRequestProvider value={value}>{children}</CatalogRequestProvider>;
}

export function DeterministicCatalogRequestProvider({
  children,
  surface,
}: PropsWithChildren<{ surface: Exclude<CatalogSurface, 'consumer'> }>): JSX.Element {
  const { dispensary: currentDispensary } = useCurrentDispensary();
  const { dispensary: legacyDispensary } = useDispensary();
  const dispensary = currentDispensary ?? legacyDispensary;
  const value = useMemo<CatalogRequestContextValue>(
    () => ({
      menuType: dispensary && isMedOnly(dispensary) ? MENU_TYPE_MED : MENU_TYPE_REC,
      orderType: '',
      ready: true,
      surface,
    }),
    [dispensary, surface]
  );

  return <CatalogRequestProvider value={value}>{children}</CatalogRequestProvider>;
}
